import { createContext, useContext, type PropsWithChildren } from "react";
import { runtimeConfig, useAccessToken } from "./auth";

type ApiClient = {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body: unknown): Promise<T>;
  put<T>(path: string, body: unknown): Promise<T>;
  delete(path: string): Promise<void>;
  download(path: string, filename: string): Promise<void>;
  upload<T>(path: string, file: File): Promise<T>;
};

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

const ApiContext = createContext<ApiClient | null>(null);

export function ApiProvider({ children }: PropsWithChildren) {
  const getToken = useAccessToken();
  const request = async <T,>(path: string, init?: RequestInit): Promise<T> => {
    const token = await getToken();
    const response = await fetch(`${runtimeConfig.apiBaseUrl}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
    if (!response.ok) {
      const detail = (await response.json().catch(() => null)) as {
        error?: string;
        code?: string;
        issues?: Array<{ path?: string; message?: string }>;
      } | null;
      const issue = detail?.issues
        ?.map(
          ({ path, message }) => `${path || "Field"}: ${message || "invalid"}`,
        )
        .join("; ");
      throw new ApiRequestError(
        issue
          ? `${detail?.error ?? "Validation failed"} — ${issue}`
          : (detail?.error ?? `Request failed (${response.status})`),
        response.status,
        detail?.code,
      );
    }
    return response.json() as Promise<T>;
  };
  const client: ApiClient = {
    get: (path) => request(path),
    post: (path, body) =>
      request(path, {
        method: "POST",
        body: body === undefined ? undefined : JSON.stringify(body),
      }),
    patch: (path, body) =>
      request(path, { method: "PATCH", body: JSON.stringify(body) }),
    put: (path, body) =>
      request(path, { method: "PUT", body: JSON.stringify(body) }),
    delete: async (path) => {
      const token = await getToken();
      const response = await fetch(`${runtimeConfig.apiBaseUrl}${path}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok && response.status !== 204)
        throw new Error(`Request failed (${response.status})`);
    },
    download: async (path, filename) => {
      const token = await getToken();
      const response = await fetch(`${runtimeConfig.apiBaseUrl}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) throw new Error(`Export failed (${response.status})`);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(await response.blob());
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    upload: async <T,>(path: string, file: File) => {
      const token = await getToken();
      const data = new FormData();
      data.append("file", file);
      const response = await fetch(`${runtimeConfig.apiBaseUrl}${path}`, {
        method: "POST",
        body: data,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) {
        const detail = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(detail?.error ?? `Upload failed (${response.status})`);
      }
      return response.json() as Promise<T>;
    },
  };
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApi() {
  const client = useContext(ApiContext);
  if (!client) throw new Error("ApiProvider is missing");
  return client;
}
